import { Spinner, Switch } from '@heroui/react';
import { BellRing, ExternalLink, Info } from 'lucide-react';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { useWebPushPermission } from '@/hooks';
import { useUserState } from '@/state';
import { detectBrowser } from '@/utils/browserInfo';
import {
  isAlertSoundEnabled,
  isSystemNotificationEnabled,
  setAlertSoundEnabled,
  setSystemNotificationEnabled,
} from '@/utils/notificationPreferences';
import { showLocalNotification } from '@/utils/webPush';

const Rows = styled.div`
  display: flex;
  flex-direction: column;
  padding: 4px 18px 8px;
  background: ${({ theme }) => (theme.mode === 'dark' ? theme.colors.background.card : '#ffffff')};
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  border-radius: 4px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 0;

  & + & {
    border-top: 1px solid ${({ theme }) => theme.colors.border.primary};
  }
`;

const Texts = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`;

const Name = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Hint = styled.span`
  font-size: 12px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Callout = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 10px;
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  border-radius: 4px;
  font-size: 12px;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.text.secondary};

  svg {
    flex-shrink: 0;
    width: 14px;
    height: 14px;
    margin-top: 2px;
  }
`;

const TestButton = styled.button`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  padding: 0;
  border: none;
  background: none;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }

  svg {
    width: 13px;
    height: 13px;
  }
`;

const HelpLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-top: 6px;
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};
  text-decoration: underline;
  text-underline-offset: 3px;

  svg {
    width: 12px;
    height: 12px;
  }
`;

/* Wynik testu ląduje POD przyciskiem, nie w toaście: dymek testowy i toast pojawiają
   się w tym samym rogu ekranu i zlewały się w jedno. */
const TestResult = styled.span<{ $error?: boolean }>`
  margin-top: 6px;
  font-size: 12px;
  line-height: 1.5;
  color: ${({ $error, theme }) => ($error ? '#dc2626' : theme.colors.text.secondary)};
`;

/**
 * Alerty panelu: dźwięk i powiadomienia systemowe.
 *
 * Ustawienie jest per przeglądarka, nie per konto — dotyczy stanowiska pracy. Włączenie
 * powiadomień wymaga zgody przeglądarki (monit tylko z kliknięcia), a po jej odrzuceniu
 * przeglądarka NIE pozwala zapytać ponownie: wtedy zamiast martwego przycisku pokazujemy
 * instrukcję odblokowania. Wyłączenie działa zawsze — gasimy je u siebie i kasujemy token
 * push, więc backend przestaje wysyłać.
 */
export const PanelNotificationsSection: FC = () => {
  const { t } = useTranslation();
  const { currentUser } = useUserState();
  const {
    permission, canAsk, isBlocked, isConfigured, request, disable,
  } = useWebPushPermission(currentUser?.uid);

  const [soundOn, setSoundOn] = useState(isAlertSoundEnabled);
  const [testState, setTestState] = useState<'idle' | 'sending' | 'sent' | 'failed'>('idle');
  // Ścieżka odblokowania różni się per przeglądarka — pokazujemy tę właściwą zamiast
  // ogólnego „sprawdź ustawienia", po którym i tak trzeba szukać samemu.
  const browser = useMemo(() => detectBrowser(), []);
  const [systemOn, setSystemOn] = useState(isSystemNotificationEnabled);

  const handleSound = (value: boolean) => {
    setSoundOn(value);
    setAlertSoundEnabled(value);
  };

  const handleSystem = async (value: boolean) => {
    if (!value) {
      setSystemOn(false);
      setSystemNotificationEnabled(false);
      await disable();
      return;
    }

    // Zgody nie ma → najpierw monit przeglądarki, dopiero po zgodzie włączamy.
    if (permission !== 'granted') {
      const result = await request();
      if (result !== 'granted') return;
    }
    setSystemOn(true);
    setSystemNotificationEnabled(true);
  };

  const showTest = permission === 'granted' && systemOn;

  return (
    <Rows>
      <Row>
        <Texts>
          <Name>{t('settings.panelNotifications.sound', 'Dźwięk powiadomień')}</Name>
          <Hint>
            {t(
              'settings.panelNotifications.soundHint',
              'Sygnał przy nowym zamówieniu, zgłoszeniu i rezerwacji. Gra także wtedy, gdy panel jest w tle — pod warunkiem że karta pozostaje otwarta.',
            )}
          </Hint>
        </Texts>
        <Switch size="sm" isSelected={soundOn} onValueChange={handleSound} aria-label={String(t('settings.panelNotifications.sound', 'Dźwięk powiadomień'))} />
      </Row>

      <Row>
        <Texts>
          <Name>{t('settings.panelNotifications.system', 'Powiadomienia systemowe')}</Name>
          <Hint>
            {t(
              'settings.panelNotifications.systemHint',
              'Dymek systemu, gdy panel jest w tle lub przeglądarka zamknięta. Dźwięk jest wtedy systemowy — własnego sygnału przeglądarka nie odtworzy.',
            )}
          </Hint>

          {isBlocked && (
            <Callout>
              <Info />
              <span>
                {browser.id === 'other'
                  ? t(
                    'settings.panelNotifications.blocked',
                    'Powiadomienia są zablokowane w przeglądarce. Kliknij kłódkę przy adresie strony, znajdź „Powiadomienia" i ustaw „Zezwalaj" — panel wykryje zmianę sam, bez odświeżania.',
                  )
                  : t(`settings.panelNotifications.unblock.${browser.id}`)}

                {browser.notificationsHelpUrl && (
                  <HelpLink href={browser.notificationsHelpUrl} target="_blank" rel="noreferrer">
                    <ExternalLink />
                    {t('settings.panelNotifications.helpLink', 'Instrukcja dla {{browser}}', { browser: browser.name })}
                  </HelpLink>
                )}
              </span>
            </Callout>
          )}

          {!isConfigured && !isBlocked && (
            <Callout>
              <Info />
              {/* Bez klucza VAPID nie ma pushy przy zamkniętej przeglądarce, ale to
                  zadanie dla nas, nie dla hotelu — komunikat nie może kazać recepcji
                  „skonfigurować środowiska". */}
              {t(
                'settings.panelNotifications.notConfigured',
                'Dymki działają, gdy panel jest otwarty — także w tle. Powiadomienia przy całkiem zamkniętej przeglądarce wymagają jeszcze dokończenia konfiguracji po stronie hotelspot; nic nie musisz w tym celu robić.',
              )}
            </Callout>
          )}

          {showTest && (
            <TestButton
              type="button"
              disabled={testState === 'sending'}
              onClick={() => {
                /* Wyświetlenie idzie przez service workera z fallbackiem na konstruktor
                   i ZAWSZE kończy się komunikatem. „Nic się nie dzieje" po kliknięciu to
                   zwykle dymek wyciszony przez system (tryb skupienia, powiadomienia
                   przeglądarki wyłączone w ustawieniach systemu), a nie błąd panelu. */
                setTestState('sending');
                showLocalNotification(
                  String(t('settings.panelNotifications.testTitle', 'Powiadomienia działają')),
                  {
                    body: String(t('settings.panelNotifications.testBody', 'Tak wygląda alert z panelu hotelspot.')),
                    tag: 'hs-test',
                    icon: '/logo192.png',
                  },
                )
                  .then((shown) => setTestState(shown ? 'sent' : 'failed'))
                  .catch(() => setTestState('failed'));
              }}
            >
              {testState === 'sending' ? <Spinner size="sm" color="current" /> : <BellRing />}
              {t('settings.panelNotifications.test', 'Wyślij testowe powiadomienie')}
            </TestButton>
          )}

          {showTest && testState === 'sent' && (
            <TestResult>
              {t(
                'settings.panelNotifications.testSent',
                'Wysłano testowe powiadomienie. Jeśli go nie widzisz, sprawdź powiadomienia przeglądarki w ustawieniach systemu (tryb skupienia / Nie przeszkadzać).',
              )}
            </TestResult>
          )}

          {showTest && testState === 'failed' && (
            <TestResult $error>
              {t(
                'settings.panelNotifications.testFailed',
                'Przeglądarka nie pozwoliła wyświetlić powiadomienia. Sprawdź uprawnienia strony i ustawienia systemowe.',
              )}
            </TestResult>
          )}
        </Texts>
        <Switch
          size="sm"
          isSelected={systemOn && permission === 'granted'}
          isDisabled={isBlocked || (!canAsk && permission !== 'granted')}
          onValueChange={handleSystem}
          aria-label={String(t('settings.panelNotifications.system', 'Powiadomienia systemowe'))}
        />
      </Row>
    </Rows>
  );
};
